import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useKameleoonInitialize } from './init';
import type { KameleoonFeatureFlags, KameleoonState, UpdateKameleoonFeaturesList } from './types';

type KameleoonContextProps = {
    state: KameleoonState;
    updateFeaturesList: UpdateKameleoonFeaturesList;
};

const useInitialization = () => {
    const [state, setState] = useState<KameleoonState>({ status: 'loading' });

    const updateFeaturesList: UpdateKameleoonFeaturesList = useCallback(
        (
            featuresOrCallback:
                | KameleoonFeatureFlags
                | ((features: KameleoonFeatureFlags) => KameleoonFeatureFlags),
        ) => {
            setState((oldState) => {
                if (oldState.status !== 'initialized') {
                    return oldState;
                }

                const newFeatures =
                    typeof featuresOrCallback === 'function'
                        ? featuresOrCallback(oldState.features)
                        : featuresOrCallback;

                return {
                    ...oldState,
                    features: newFeatures,
                };
            });
        },
        [],
    );

    useKameleoonInitialize({ setKameleoonState: setState });

    const contextValue = useMemo(() => {
        return {
            updateFeaturesList,
            state,
        };
    }, [state, updateFeaturesList]);

    return contextValue;
};

export const KameleoonFeaturesContext = createContext<KameleoonContextProps>({
    state: { status: 'loading' },
    updateFeaturesList: () => undefined,
});

export const useKameleoonState = () => {
    const context = useContext(KameleoonFeaturesContext);

    if (!context) {
        throw new Error(`Hook should be used within context provider`);
    }

    return context;
};

export const KameleoonFeaturesProvider = ({ children }: { children: React.ReactNode }) => {
    const value = useInitialization();

    return (
        <KameleoonFeaturesContext.Provider value={value}>
            {children}
        </KameleoonFeaturesContext.Provider>
    );
};
